import { SUPPORTED_COUNTRIES } from '../constants/country';
import { Country, Address, DetailsResponseItem, WoosmapType, Zone } from '../types/commons';
import { DEFAULT_ZONE, GB_AREA_CODE_REGEX } from '../constants/zone';

import { isCountryGB } from './country';

type AddressNameKeys = 'long_name' | 'short_name';

export const findAddressComponent = (
  place: Partial<DetailsResponseItem>,
  key: string,
  name: AddressNameKeys = 'long_name',
) => {
  const component = place?.address_components?.find(({ types }) => types.includes(key));
  return component?.[name];
};

export const isSupportedAddress = (supportedAddressTypes: string[], types?: string[]) =>
  types && types.some((type) => supportedAddressTypes.includes(type));

export const isSupportedCountry = (country?: string) =>
  country ? SUPPORTED_COUNTRIES.includes(country as Country) : false;

const SUPPORTED_WOOSMAP_ADDRESS_TYPES: WoosmapType[] = ['address'];

export const isValidWoosmapAddress = (
  localityDetails: Partial<DetailsResponseItem>,
): localityDetails is DetailsResponseItem =>
  !!(
    isSupportedAddress(SUPPORTED_WOOSMAP_ADDRESS_TYPES, localityDetails.types) &&
    isSupportedCountry(findAddressComponent(localityDetails, 'country', 'short_name')) &&
    !!findAddressComponent(localityDetails, 'route') &&
    (!!findAddressComponent(localityDetails, 'locality') || !!findAddressComponent(localityDetails, 'postal_town')) &&
    !!findAddressComponent(localityDetails, 'postal_codes') &&
    !!localityDetails.public_id &&
    localityDetails.formatted_address &&
    localityDetails.geometry?.location.lat &&
    localityDetails.geometry.location.lng
  );

export const buildWoosmapAddress = (localityDetails: DetailsResponseItem): Address => ({
  address: localityDetails.formatted_address,
  street_name: findAddressComponent(localityDetails, 'route'),
  street_number:
    findAddressComponent(localityDetails, 'street_number') || findAddressComponent(localityDetails, 'premise'),
  city: findAddressComponent(localityDetails, 'locality') || findAddressComponent(localityDetails, 'postal_town'),
  zip_code: findAddressComponent(localityDetails, 'postal_codes'),
  lat: localityDetails.geometry.location.lat,
  lng: localityDetails.geometry.location.lng,
});

export const zoneForZipCode = (zipCode: string, zones?: Zone[]) => {
  if (!zipCode || zipCode.length < 2 || !zones?.length) return DEFAULT_ZONE;

  let department: string;

  if (isCountryGB()) {
    const departmentMatches = zipCode.match(GB_AREA_CODE_REGEX);

    if (!departmentMatches?.length) return DEFAULT_ZONE;

    // The destructuring assignment looks less readable to me
    // eslint-disable-next-line prefer-destructuring
    department = departmentMatches[0];
  } else {
    department = zipCode.substring(0, 2);
  }

  // search first by zip code, then by department
  return (
    zones.find((zone) => zone.area_codes.includes(zipCode)) ||
    zones.find((zone) => zone.area_codes.includes(department)) ||
    zones[0]
  ).number;
};
